/**
 * Sign in.
 *
 * The email carries one challenge in two forms: a link, and a short code. In
 * the default single-user deployment there is no mail provider configured, so
 * the API answers `delivered: 'response'` and hands both straight back.
 * Pretending an email was sent in that case would leave the user waiting for
 * something that is never going to arrive, so they are shown as what they are.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { auth } from '../lib/api';
import { ErrorNotice, Spinner } from '../components/Feedback';
import { Icon } from '../components/Icon';
import { useSession } from '../lib/session';
import { navigate } from '../router/router';

type Sent = { delivered: 'email' | 'response'; link?: string; code?: string };

export function SignIn() {
  const { reload } = useSession();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [sent, setSent] = useState<Sent | null>(null);

  const requestLink = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      setSent(await auth.requestLink(email.trim()));
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (event?: FormEvent) => {
    event?.preventDefault();
    if (busy || !email.trim() || !code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await auth.verifyCode(email.trim(), code.trim());
      await reload();
      navigate('/', { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <div className="wrap wrap--narrow stack" style={{ paddingTop: 'var(--s-8)' }}>
        <div className="row" style={{ gap: 'var(--s-3)' }}>
          <Icon name="tv" size={22} />
          <h1>SFLX Live</h1>
        </div>
        <p className="muted">
          Sign in with a one-time link or code sent to your email. No password is stored, and
          either one works once. If you were invited, use the address that received the invite.
        </p>

        {sent ? (
          <div className="card stack">
            {sent.delivered === 'response' && sent.link ? (
              <>
                <h2>Your sign-in is ready</h2>
                <p className="muted">
                  No mail provider is configured on this deployment, so the link and code are
                  shown here instead of being emailed. Either one signs you in, and using one
                  uses up the other.
                </p>
                <a className="btn btn--primary" href={sent.link}>
                  <Icon name="link" size={16} />
                  Open sign-in link
                </a>
                {sent.code ? (
                  <p className="signin-code">
                    <span className="sr-only">Code </span>
                    {sent.code}
                  </p>
                ) : null}
              </>
            ) : (
              <>
                <h2>Check your inbox</h2>
                <p className="muted">
                  A sign-in link and code are on its way to {email}. They expire in 15 minutes.
                  Open the link, or type the code below.
                </p>
              </>
            )}
            <CodeForm code={code} setCode={setCode} busy={busy} error={error} onSubmit={submitCode} />
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSent(null)}>
              Use a different address
            </button>
          </div>
        ) : (
          <div className="stack">
            <ErrorNotice error={error} />
            <form className="card stack" onSubmit={requestLink}>
              <div className="field">
                <label htmlFor="signin-email">Email</label>
                <input
                  id="signin-email"
                  className="input"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <button type="submit" className="btn btn--primary" disabled={busy}>
                {busy ? <Spinner /> : null}
                Email me a sign-in
              </button>
            </form>
            <form className="card stack" onSubmit={(event) => void submitCode(event)}>
              <div className="field">
                <label htmlFor="signin-code">Sign-in code</label>
                <input
                  id="signin-code"
                  className="input input--code"
                  type="text"
                  inputMode="text"
                  autoComplete="one-time-code"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="XXXX-XXXX"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </div>
              <p className="muted">Already have a code? Enter it with the same email.</p>
              <button type="submit" className="btn" disabled={busy || !email.trim() || !code.trim()}>
                {busy ? <Spinner /> : null}
                Sign in with code
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

function CodeForm({
  code,
  setCode,
  busy,
  error,
  onSubmit,
}: {
  code: string;
  setCode: (value: string) => void;
  busy: boolean;
  error: unknown;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <form className="stack" onSubmit={onSubmit}>
      <div className="field">
        <label htmlFor="signin-code-sent">Sign-in code</label>
        <input
          id="signin-code-sent"
          className="input input--code"
          type="text"
          inputMode="text"
          autoComplete="one-time-code"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          placeholder="XXXX-XXXX"
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
      </div>
      <ErrorNotice error={error} />
      <button type="submit" className="btn" disabled={busy || !code.trim()}>
        {busy ? <Spinner /> : null}
        Sign in with code
      </button>
    </form>
  );
}
